import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { ErrorBox, Loader, PageHeader } from '../../components/Common';
import { useAuthStore } from '../../store';
import type { MasterSettings } from '../../types';

const DEFAULTS: MasterSettings = {
  slot_step_minutes: 30,
  buffer_minutes: 15,
  timezone: 'Europe/Moscow',
  auto_confirm: false,
  max_advance_days: 30,
  min_cancel_hours: 4,
  auto_reject_hours: 24,
};

export function MasterSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-settings', user?.id],
    queryFn: () => api.masters.settings(user!.id),
    enabled: !!user,
  });

  const [form, setForm] = useState<MasterSettings>(DEFAULTS);
  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.masters.updateSettings(user!.id, form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-settings'] });
    },
  });

  if (isLoading) return <Loader />;
  if (error) return <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />;

  return (
    <div>
      <PageHeader title="Настройки" subtitle="Параметры записи и слотов" />
      <div className="card space-y-3">
        <div>
          <label className="label">Шаг слота, мин</label>
          <select
            className="input"
            value={form.slot_step_minutes}
            onChange={(e) => setForm({ ...form, slot_step_minutes: Number(e.target.value) })}
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={60}>60</option>
          </select>
        </div>
        <Field
          label="Буфер между записями, мин"
          value={form.buffer_minutes}
          onChange={(v) => setForm({ ...form, buffer_minutes: v })}
          min={0}
          max={120}
        />
        <Field
          label="Окно записи, дней вперёд"
          value={form.max_advance_days}
          onChange={(v) => setForm({ ...form, max_advance_days: v })}
          min={1}
          max={365}
        />
        <Field
          label="Минимум часов до отмены"
          value={form.min_cancel_hours}
          onChange={(v) => setForm({ ...form, min_cancel_hours: v })}
          min={0}
          max={72}
        />
        <Field
          label="Авто-отклонение через, ч"
          value={form.auto_reject_hours}
          onChange={(v) => setForm({ ...form, auto_reject_hours: v })}
          min={1}
          max={168}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.auto_confirm}
            onChange={(e) => setForm({ ...form, auto_confirm: e.target.checked })}
          />
          Авто-подтверждение записей
        </label>
      </div>

      {save.error && <ErrorBox message={(save.error as Error).message} />}

      <button
        className="btn-primary mt-4 w-full"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Сохраняем...' : save.isSuccess ? 'Сохранено ✓' : 'Сохранить'}
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type="number"
        className="input"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}
